import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RefreshIcon from '@mui/icons-material/Refresh';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { DATABASE, STORAGE } from '../../../Config/firebase';

const LONDON_TZ = 'Europe/London';

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatLondon = (value) => {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: LONDON_TZ,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (sec) => {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}m ${rem}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const locationLabel = (row) => {
  const parts = [row.city, row.region, row.country].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

const londonDayKey = (value) => {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString('en-CA', { timeZone: LONDON_TZ });
};

const formatCpf = (digits) => {
  const d = String(digits || '').replace(/\D/g, '');
  if (d.length !== 11) return digits || '—';
  return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const shortHash = (h) => (h ? `${String(h).slice(0, 10)}…${String(h).slice(-6)}` : '—');

const KatuVisits = () => {
  const isMobile = useMediaQuery('(max-width:900px)');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tick, setTick] = useState(0);
  const [agreements, setAgreements] = useState([]);
  const [agreementsLoading, setAgreementsLoading] = useState(true);
  const [agreementsError, setAgreementsError] = useState(null);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    const q = query(collection(DATABASE, 'katu_agreements'), orderBy('signedAt', 'desc'), limit(100));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setAgreements(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setAgreementsLoading(false);
        setAgreementsError(null);
      },
      (err) => {
        console.error('katu_agreements listen:', err);
        setAgreementsError(err.message || 'Failed to load agreements');
        setAgreementsLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const downloadAgreementPdf = async (row) => {
    if (!row?.storagePath) return;
    setDownloadingId(row.id);
    try {
      const url = await getDownloadURL(storageRef(STORAGE, row.storagePath));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('katu agreement download:', err);
      setAgreementsError(`Could not open PDF for ${row.id}: ${err.message || err.code || 'unknown error'}`);
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(DATABASE, 'katu_sessions'),
      orderBy('lastSeenAt', 'desc'),
      limit(200)
    );
    const unsubSessions = onSnapshot(
      q,
      (snap) => {
        setSessions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('katu_sessions listen:', err);
        setError(err.message || 'Failed to load sessions');
        setLoading(false);
      }
    );
    return () => {
      unsubSessions();
    };
  }, []);

  const stats = useMemo(() => {
    void tick;
    const now = Date.now();
    const todayKey = londonDayKey(new Date());
    const weekAgo = now - 7 * 24 * 60 * 60 * 1000;
    let today = 0;
    let week = 0;
    let durationSum = 0;
    const countries = new Set();

    sessions.forEach((s) => {
      const last = toDate(s.lastSeenAt);
      const lastMs = last?.getTime() || 0;
      if (last && londonDayKey(last) === todayKey) today += 1;
      if (lastMs >= weekAgo) week += 1;
      durationSum += Number(s.durationSec) || 0;
      if (s.country) countries.add(s.country);
    });

    return {
      today,
      week,
      avgDuration: sessions.length ? durationSum / sessions.length : 0,
      countries: countries.size,
    };
  }, [sessions, tick]);

  const cardSx = {
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 2,
    height: '100%',
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TravelExploreIcon sx={{ color: '#2AF598' }} />
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
              KATU Visits
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
              Live sessions for{' '}
              <Box
                component="a"
                href="https://fiddux.com/katu/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#2AF598' }}
              >
                fiddux.com/katu
              </Box>
              {' '}· times in Europe/London
            </Typography>
          </Box>
        </Box>
        <Chip
          icon={<RefreshIcon sx={{ fontSize: 16 }} />}
          label={loading ? 'Loading…' : 'Live'}
          size="small"
          sx={{
            backgroundColor: loading ? 'rgba(255,255,255,0.12)' : 'rgba(42,245,152,0.15)',
            color: '#fff',
            border: '1px solid rgba(42,245,152,0.4)',
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Sessions today', value: stats.today },
          { label: 'Sessions (7 days)', value: stats.week },
          { label: 'Avg duration', value: formatDuration(stats.avgDuration) },
          { label: 'Countries', value: stats.countries },
        ].map((c) => (
          <Grid item xs={6} md={3} key={c.label}>
            <Card sx={cardSx}>
              <CardContent>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>
                  {c.label}
                </Typography>
                <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                  {loading ? '…' : c.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper
        sx={{
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: '#2AF598' }} />
          </Box>
        ) : sessions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
              No visits recorded yet. Open{' '}
              <Box component="a" href="https://fiddux.com/katu/" target="_blank" rel="noopener noreferrer" sx={{ color: '#2AF598' }}>
                /katu
              </Box>{' '}
              to generate the first session.
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {sessions.map((row) => (
              <Card
                key={row.id}
                onClick={() => setSelected(row)}
                sx={{ ...cardSx, cursor: 'pointer' }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                    {formatLondon(row.lastSeenAt)}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                    {locationLabel(row)} · {formatDuration(row.durationSec)} · {row.device || '—'}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, mt: 0.5 }}>
                    Scroll {Math.round(Number(row.scrollMaxPct) || 0)}%
                    {row.hash ? ` · ${row.hash}` : ''}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Started', 'Last seen', 'Duration', 'Location', 'Device', 'Referrer', 'Scroll'].map((h) => (
                    <TableCell key={h} sx={{ color: 'rgba(255,255,255,0.65)', borderColor: 'rgba(255,255,255,0.08)' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => setSelected(row)}
                    sx={{ cursor: 'pointer', '& td': { color: '#fff', borderColor: 'rgba(255,255,255,0.06)' } }}
                  >
                    <TableCell>{formatLondon(row.startedAt)}</TableCell>
                    <TableCell>{formatLondon(row.lastSeenAt)}</TableCell>
                    <TableCell>{formatDuration(row.durationSec)}</TableCell>
                    <TableCell>{locationLabel(row)}</TableCell>
                    <TableCell>
                      {[row.device, row.os, row.browser].filter(Boolean).join(' · ') || '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.referrer || '(direct)'}
                    </TableCell>
                    <TableCell>{Math.round(Number(row.scrollMaxPct) || 0)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ---------------- Signed agreements (Term Sheet) ---------------- */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HistoryEduIcon sx={{ color: '#2AF598' }} />
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
              Signed agreements
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
              Term Sheet signed · online signing closed · read-only archive · PDF stored privately
            </Typography>
          </Box>
        </Box>
        <Chip
          label={agreementsLoading ? 'Loading…' : `${agreements.length} signed`}
          size="small"
          sx={{
            backgroundColor: agreements.length ? 'rgba(42,245,152,0.15)' : 'rgba(255,255,255,0.12)',
            color: '#fff',
            border: '1px solid rgba(42,245,152,0.4)',
          }}
        />
      </Box>

      {agreementsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAgreementsError(null)}>
          {agreementsError}
        </Alert>
      )}

      <Paper
        sx={{
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {agreementsLoading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: '#2AF598' }} />
          </Box>
        ) : agreements.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
              No agreement on record.
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {agreements.map((row) => (
              <Card key={row.id} onClick={() => setSelectedAgreement(row)} sx={{ ...cardSx, cursor: 'pointer' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                      {row.signer?.name || '—'}
                    </Typography>
                    <Chip
                      icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                      label={row.status === 'signed' ? 'Signed' : row.status || '—'}
                      size="small"
                      sx={{ backgroundColor: 'rgba(42,245,152,0.15)', color: '#fff', border: '1px solid rgba(42,245,152,0.4)', height: 22 }}
                    />
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                    {row.signer?.role || '—'} · {row.company?.name || '—'}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, mt: 0.5 }}>
                    {formatLondon(row.signedAt)} · {row.id}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    disabled={downloadingId === row.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadAgreementPdf(row);
                    }}
                    sx={{ mt: 1, color: '#2AF598', textTransform: 'none', px: 0 }}
                  >
                    {downloadingId === row.id ? 'Opening…' : 'Open PDF'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Signed at', 'ID', 'Signer', 'Role', 'E-mail', 'Version', 'PDF SHA-256', 'E-mails', ''].map((h) => (
                    <TableCell key={h || 'actions'} sx={{ color: 'rgba(255,255,255,0.65)', borderColor: 'rgba(255,255,255,0.08)' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {agreements.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => setSelectedAgreement(row)}
                    sx={{ cursor: 'pointer', '& td': { color: '#fff', borderColor: 'rgba(255,255,255,0.06)' } }}
                  >
                    <TableCell>{formatLondon(row.signedAt)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.id}</TableCell>
                    <TableCell>{row.signer?.name || '—'}</TableCell>
                    <TableCell>{row.signer?.role || '—'}</TableCell>
                    <TableCell>{row.signer?.email || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.termsVersion || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      <Tooltip title={row.pdfSha256 || ''}>
                        <span>{shortHash(row.pdfSha256)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      client: {row.emails?.client || '—'}
                      <br />
                      admin: {row.emails?.admin || '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Open signed PDF">
                        <IconButton
                          size="small"
                          disabled={downloadingId === row.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadAgreementPdf(row);
                          }}
                          sx={{ color: '#2AF598' }}
                        >
                          {downloadingId === row.id ? <CircularProgress size={16} sx={{ color: '#2AF598' }} /> : <DownloadIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={!!selectedAgreement}
        onClose={() => setSelectedAgreement(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0d1713',
            border: '1px solid rgba(255,255,255,0.12)',
            color: '#fff',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Agreement detail
          <IconButton onClick={() => setSelectedAgreement(null)} sx={{ color: '#fff' }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          {selectedAgreement && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                ['ID', selectedAgreement.id],
                ['Status', selectedAgreement.status || '—'],
                ['Document', selectedAgreement.documentTitle || '—'],
                ['Version', selectedAgreement.termsVersion || '—'],
                ['Company', `${selectedAgreement.company?.name || '—'} · CNPJ ${selectedAgreement.company?.cnpj || '—'}`],
                ['Signer', `${selectedAgreement.signer?.name || '—'} — ${selectedAgreement.signer?.role || '—'}`],
                ['CPF', formatCpf(selectedAgreement.signer?.cpf)],
                ['E-mail', selectedAgreement.signer?.email || '—'],
                ['Phone', selectedAgreement.signer?.phone || '—'],
                ['Signed at', formatLondon(selectedAgreement.signedAt)],
                ['IP', selectedAgreement.ip || '—'],
                ['Timezone', selectedAgreement.timezone || '—'],
                ['User-Agent', selectedAgreement.userAgent || '—'],
                ['PDF SHA-256', selectedAgreement.pdfSha256 || '—'],
                ['Content SHA-256', selectedAgreement.contentSha256 || '—'],
                ['E-mail client', selectedAgreement.emails?.client || '—'],
                ['E-mail admin', selectedAgreement.emails?.admin || '—'],
                ['Storage path', selectedAgreement.storagePath || '—'],
              ].map(([k, v]) => (
                <Box key={k} sx={{ display: 'flex', gap: 2 }}>
                  <Typography sx={{ width: 130, flexShrink: 0, color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                    {k}
                  </Typography>
                  <Typography sx={{ color: '#fff', fontSize: 13, wordBreak: 'break-all' }}>{v}</Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadIcon />}
                  disabled={downloadingId === selectedAgreement.id}
                  onClick={() => downloadAgreementPdf(selectedAgreement)}
                  sx={{ backgroundColor: '#2AF598', color: '#04140d', textTransform: 'none', fontWeight: 700, '&:hover': { backgroundColor: '#00BA2D' } }}
                >
                  Open signed PDF
                </Button>
                {selectedAgreement.verifyUrl && (
                  <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href={selectedAgreement.verifyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<VerifiedIcon />}
                    sx={{ color: '#2AF598', borderColor: 'rgba(42,245,152,0.4)', textTransform: 'none' }}
                  >
                    Verification page
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0d1713',
            border: '1px solid rgba(255,255,255,0.12)',
            color: '#fff',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Session detail
          <IconButton onClick={() => setSelected(null)} sx={{ color: '#fff' }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          {selected && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                ['Session ID', selected.id],
                ['Started', formatLondon(selected.startedAt)],
                ['Last seen', formatLondon(selected.lastSeenAt)],
                ['Duration', formatDuration(selected.durationSec)],
                ['Location', locationLabel(selected)],
                ['IP (truncated)', selected.ipTruncated || '—'],
                ['Device', [selected.device, selected.os, selected.browser].filter(Boolean).join(' · ') || '—'],
                ['Screen', selected.screenW && selected.screenH ? `${selected.screenW}×${selected.screenH}` : '—'],
                ['Language', selected.language || '—'],
                ['Timezone', selected.timezone || '—'],
                ['Referrer', selected.referrer || '(direct)'],
                ['Query', selected.query || '—'],
                ['Hash / section', selected.hash || '—'],
                ['Scroll max', `${Math.round(Number(selected.scrollMaxPct) || 0)}%`],
                ['Last event', selected.lastEvent || '—'],
              ].map(([k, v]) => (
                <Box key={k} sx={{ display: 'flex', gap: 2 }}>
                  <Typography sx={{ width: 130, flexShrink: 0, color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                    {k}
                  </Typography>
                  <Typography sx={{ color: '#fff', fontSize: 13, wordBreak: 'break-all' }}>{v}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default KatuVisits;
